import { useMutation, useQueryClient } from '@tanstack/react-query';

import { activateBom } from '#/api/momMgmt/settings/bom/activate';
import { deactivateBom } from '#/api/momMgmt/settings/bom/deactivate';
import {
  getBomApiErrorMessage,
  isBomVersionConflict,
} from '#/modules/momMgmt/settings/bom/helper/apiError';
import { toast } from '#/utils/toast';
import {
  bomDetailQueryKey,
  bomListQueryKeyPrefix,
} from '#/utils/queryKeys';

/** 與 CreateEdit／SideMsg 文案一致。 */
export const BOM_ACTIVATE_CONFIRM = {
  title: '啟用 BOM',
  message: '確定啟用此 BOM？啟用後可被新工單選用。',
  confirmText: '啟用',
};

export const BOM_DEACTIVATE_CONFIRM = {
  title: '停用 BOM',
  message: '確定停用此 BOM？停用後仍可讀取，但不可被新工單選用。',
  confirmText: '停用',
};

/**
 * BOM 啟用／停用 mutation（confirm 由呼叫端處理；文案見 BOM_*_CONFIRM）。
 *
 * @param {object} options
 * @param {string} options.id
 * @param {() => string|null|undefined} options.getUpdatedAt
 * @param {() => void|Promise<void>} [options.onAfterSuccess]
 * @param {() => void|Promise<void>} [options.onVersionConflict]
 */
function useBomStatusMutation({
  id,
  getUpdatedAt,
  onAfterSuccess,
  onVersionConflict,
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ action }) => {
      const payload = { updatedAt: getUpdatedAt?.() };
      if (action === 'activate') {
        return activateBom(id, payload);
      }
      if (action === 'deactivate') {
        return deactivateBom(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result) => {
      toast.success(result?.message || '操作成功');
      await queryClient.invalidateQueries({ queryKey: bomListQueryKeyPrefix() });
      if (id) {
        await queryClient.invalidateQueries({ queryKey: bomDetailQueryKey(id) });
      }
      await onAfterSuccess?.(result);
    },
    onError: (error) => {
      toast.error(getBomApiErrorMessage(error));
      if (isBomVersionConflict(error)) {
        onVersionConflict?.(error);
      }
    },
  });
}

export default useBomStatusMutation;
